import { useModalStore } from '@stores/modal'
import { getModalComponent } from '../config/modal-registry'
import { getModalContentWithOverrides } from '../config/modal-content'

// Every show method returns a promise for what the member did: 'primary' or 'secondary'
// for the action they took, undefined when the modal was dismissed, hidden or replaced.
// Callers await it instead of passing callbacks, so modal data stays serialisable.
class ModalService {
    constructor() {
        this._settle = null
    }

    _open(type, data, storeAction) {
        if (!getModalComponent(type)) {
            console.error(`Modal type "${type}" not found in registry`)
            return Promise.resolve()
        }

        this._settlePending()
        useModalStore()[storeAction]({ type, data })
        return new Promise(resolve => {
            this._settle = resolve
        })
    }

    _settlePending(result) {
        const settle = this._settle
        this._settle = null
        settle?.(result)
    }

    /**
     * Show a modal by content key
     * @param {string} contentKey - The key from MODAL_CONTENT
     * @param {object} overrides - Optional data overrides
     * @returns {Promise<'primary' | 'secondary' | undefined>}
     */
    show(contentKey, overrides = {}) {
        const content = getModalContentWithOverrides(contentKey, overrides)
        return this._open(content.type, content, 'showModal')
    }

    /**
     * Show a modal with custom data (bypasses content configuration)
     * @param {string} type - The modal type from registry
     * @param {object} data - Complete data for the modal
     * @returns {Promise<'primary' | 'secondary' | undefined>}
     */
    showCustom(type, data) {
        return this._open(type, data, 'showModal')
    }

    /**
     * Close the open modal, settling its promise with what the member did
     * @param {'primary' | 'secondary'} [result] - The action taken; omitted for a dismissal
     */
    close(result) {
        this._settlePending(result)
        useModalStore().hideModal()
    }

    /**
     * Hide the currently open modal
     */
    hide() {
        this.close()
    }

    /**
     * Replace the current modal with a new one
     * @param {string} contentKey - The key from MODAL_CONTENT
     * @param {object} overrides - Optional data overrides
     * @returns {Promise<'primary' | 'secondary' | undefined>}
     */
    replace(contentKey, overrides = {}) {
        const content = getModalContentWithOverrides(contentKey, overrides)
        return this._open(content.type, content, 'replaceModal')
    }

    /**
     * Update data for the currently open modal
     * @param {object} data - Data to merge with existing modal data
     */
    updateData(data) {
        useModalStore().updateModalData(data)
    }

    // Legacy methods for backward compatibility (will be removed after migration)
    showModal(type, data = {}) {
        console.warn('modalService.showModal() is deprecated. Use modalService.show() or modalService.showCustom()')
        return this.showCustom(type, data)
    }

    hideModal() {
        console.warn('modalService.hideModal() is deprecated. Use modalService.hide()')
        this.hide()
    }

    showAuthPrompt(data = {}) {
        console.warn('modalService.showAuthPrompt() is deprecated. Use modalService.show("AUTH_REQUIRED_FOR_PLAYBACK")')
        return this.show('AUTH_REQUIRED_FOR_PLAYBACK', data)
    }

    showPremiumUpsell(data = {}) {
        console.warn('modalService.showPremiumUpsell() is deprecated. Use modalService.show("PREMIUM_UPSELL_GENERAL")')
        return this.show('PREMIUM_UPSELL_GENERAL', data)
    }

    showError(data = {}) {
        console.warn('modalService.showError() is deprecated. Use modalService.show("ERROR_GENERIC")')
        return this.show('ERROR_GENERIC', data)
    }

    showConfirmation(data = {}) {
        console.warn('modalService.showConfirmation() is deprecated. Use modalService.show("CONFIRM_DELETE")')
        return this.show('CONFIRM_DELETE', data)
    }
}

export default new ModalService()
